import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Search, X, ShoppingBag, MapPin, Package, Building2,
  Check, Gem, Phone, User, RefreshCw, Filter, Tag,
  CheckCircle, ArrowLeft, Store, Globe, Banknote,
  CreditCard, FileText, Truck
} from 'lucide-react';
import { API_URL } from '../../services/api';
import { notifySuccess, notifyError } from '../../utils/notifications';
import { canSeeTypeCaisse } from '../../utils/typeCaisseLabels';


const TYPE_GRAD = {
  'BAGUE': 'from-violet-500 to-purple-700',
  'BRACELET': 'from-sky-500 to-blue-700',
  'COLLIER': 'from-amber-500 to-orange-600',
  'BOUCLE OREILLE': 'from-rose-500 to-pink-700',
  'BO': 'from-rose-500 to-pink-700',
  'PENDENTIF': 'from-emerald-500 to-teal-700',
};
const TYPE_ICON = {
  'BAGUE': '💍', 'BRACELET': '📿', 'COLLIER': '💛',
  'BOUCLE OREILLE': '✨', 'BO': '✨', 'PENDENTIF': '💎',
};
const PAY_METHODS = [
  { id: 'especes',  label: 'Especes',  icon: '💵' },
  { id: 'carte',    label: 'Carte',    icon: '💳' },
  { id: 'cheque',   label: 'Cheque',  icon: '📄' },
  { id: 'virement', label: 'Virement', icon: '🏦' },
  { id: 'or',       label: 'Or',       icon: '🥇' },
];

function fmt(n) {
  if (!n && n !== 0) return '—';
  return parseFloat(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function round2(n) { return Math.round(parseFloat(n || 0) * 100) / 100; }

export default function VenteBoutique() {
  // ─── STATE ───────────────────────────────────────────────────────────────
  const [boutiques, setBoutiques]     = useState([]);
  const [boutiqueId, setBoutiqueId]   = useState(() => {
    try {
      const ctx = JSON.parse(localStorage.getItem('workspaceContext') || '{}');
      return ctx.boutique_id || null;
    } catch { return null; }
  });
  const [modeles, setModeles]         = useState([]);
  const [collections, setCollections] = useState([]);
  const [types, setTypes]             = useState([]);
  const [loading, setLoading]         = useState(false);

  // §201 — type_caisse : sélecteur admin only ; vendeuse => masqué, défaut 'officielle'
  const showTypeCaisse = (() => {
    try { return canSeeTypeCaisse(JSON.parse(localStorage.getItem('user') || '{}')?.role || ''); }
    catch { return false; }
  })();

  const [search, setSearch]       = useState('');
  const [fCol, setFCol]           = useState('');
  const [fType, setFType]         = useState('');
  const [fDispo, setFDispo]       = useState('tous'); // tous | ici | reseau

  const [selectedCollection, setSelectedCollection] = useState(null); // null=vue collections
  const [selectedModele, setSelectedModele] = useState(null);
  const [panelPhase, setPanelPhase]   = useState(1); // 1=fiche produit 2=disponibilités
  const [salePiece, setSalePiece]     = useState(null);
  const [isCommande, setIsCommande]   = useState(false);
  const [saleForm, setSaleForm]       = useState({ client_nom:'', client_tel:'', prix:'', remise:'0', paiement:'especes', type_caisse:'officielle', obs:'' });
  const [paiements, setPaiements]     = useState([]); // multi-payment: [{mode, montant, reference, or_poids, or_prix_g}]
  const [selling, setSelling]         = useState(false);
  const [saleSuccess, setSaleSuccess] = useState(null);
  const [transfertEnCours, setTransfertEnCours] = useState(false);
  const [saleStep, setSaleStep] = useState(1); // 1=formulaire 2=récapitulatif
  const [ventePhase, setVentePhase] = useState('client'); // 'client' → 'confirm' → 'catalogue' → sale

  // ─── CLIENT AUTOCOMPLETE ──────────────────────────────────────────────────
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSugg, setShowClientSugg] = useState(false);
  const clientSearchTimer = useRef(null);
  const [selectedClient, setSelectedClient] = useState(null); // full client object with solde_or_grammes, solde_compte

  // ─── NOUVEAU CLIENT INLINE ────────────────────────────────────────────────
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientInSale, setNewClientInSale] = useState({ nom: '', telephone: '' });
  const [savingNewClient, setSavingNewClient] = useState(false);

  // ─── MODAL TRANSFERT CLIENT ───────────────────────────────────────────────
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [pendingTransfert, setPendingTransfert] = useState(null); // { piece, modele, type: 'reseau'|'commande' }

  // ─── LOAD DATA ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_URL}/boutiques`)
      .then(r => r.json()).then(d => setBoutiques(Array.isArray(d) ? d : []))
      .catch(err => console.error('Erreur:', err));
    fetch(`${API_URL}/bijoux/catalogue/collections`)
      .then(r => r.json()).then(d => {
        setCollections(Array.isArray(d.collections) ? d.collections : []);
        setTypes(Array.isArray(d.types) ? d.types : []);
      }).catch(err => console.error('Erreur:', err));
    loadModeles();
  }, []);

  const loadModeles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/bijoux/catalogue/modeles`);
      const data = await r.json();
      setModeles(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  // ─── COMPUTED ─────────────────────────────────────────────────────────────
  const boutique = boutiques.find(b => b.id === boutiqueId);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return modeles.filter(m => {
      if (term && !(
        m.reference_article?.toLowerCase().includes(term) ||
        m.nom_modele?.toLowerCase().includes(term) ||
        m.collection_nom?.toLowerCase().includes(term) ||
        m.type_bijou?.toLowerCase().includes(term) ||
        (m.pieces || []).some(p =>
          (p.id_produit || '').toString().toLowerCase().includes(term) ||
          (p.reference || '').toLowerCase().includes(term)
        )
      )) return false;
      // Filtre collection sélectionnée
      if (selectedCollection && !search) {
        if (selectedCollection === '_aucune_') { if (m.collection_nom) return false; }
        else if (m.collection_nom !== selectedCollection) return false;
      }
      if (fCol  && m.collection_nom !== fCol)  return false;
      if (fType && m.type_bijou     !== fType) return false;
      if (fDispo === 'ici' && boutiqueId)
        return (m.pieces || []).some(p => p.boutique_id === boutiqueId && p.inventaire_actif);
      if (fDispo === 'reseau')
        return (m.pieces || []).some(p => p.inventaire_actif);
      return true;
    });
  }, [modeles, search, fCol, fType, fDispo, boutiqueId, selectedCollection]);

  const stats = useMemo(() => {
    let ici = 0, reseau = 0;
    modeles.forEach(m => {
      (m.pieces || []).forEach(p => {
        if (!p.inventaire_actif) return;
        if (boutiqueId && p.boutique_id === boutiqueId) ici++;
        else reseau++;
      });
    });
    return { ici, reseau, total: modeles.length };
  }, [modeles, boutiqueId]);

  // ─── CLIENT AUTOCOMPLETE ──────────────────────────────────────────────────
  const searchClients = useCallback((term) => {
    if (!term || term.length < 2) { setClientSuggestions([]); setShowClientSugg(false); return; }
    clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/clients?search=${encodeURIComponent(term)}&limit=6`);
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.clients || []);
        setClientSuggestions(list);
        setShowClientSugg(list.length > 0);
      } catch {}
    }, 200);
  }, []);

  const selectClient = async (client) => {
    setSaleForm(f => ({
      ...f,
      client_nom: client.nom || '',
      client_tel: client.telephone || client.tel || '',
    }));
    setShowClientSugg(false);
    setClientSuggestions([]);
    setShowNewClientForm(false);
    // Fetch fresh client data with balances
    if (client.id) {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/clients/${client.id}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (r.ok) {
          const full = await r.json();
          setSelectedClient(full);
          setSaleForm(f => ({...f, client_nom: full.nom || '', client_tel: full.telephone || ''}));
          setVentePhase('catalogue');
          return;
        }
      } catch {}
    }
    setSelectedClient(client);
    setSaleForm(f => ({...f, client_nom: client.nom || '', client_tel: client.telephone || ''}));
    setVentePhase('catalogue');
  };

  const createClientInSale = async () => {
    if (!newClientInSale.nom.trim()) return;
    setSavingNewClient(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ nom: newClientInSale.nom.trim(), telephone: newClientInSale.telephone.trim() }),
      });
      const data = await r.json();
      if (r.ok) {
        selectClient({ nom: newClientInSale.nom.trim(), telephone: newClientInSale.telephone.trim(), ...data });
        setNewClientInSale({ nom: '', telephone: '' });
        setShowNewClientForm(false);
      } else {
        alert(data.error || 'Erreur création client');
      }
    } catch { alert('Erreur réseau'); }
    setSavingNewClient(false);
  };

  const resetVente = () => {
    setVentePhase('client');
    setSelectedClient(null);
    setSalePiece(null);
    setSaleSuccess(null);
    setSelectedModele(null);
    setSearch('');
    setSaleForm({ client_nom:'', client_tel:'', prix:'', remise:'0', paiement:'especes', type_caisse:'officielle', obs:'' });
    setPaiements([]);
  };

  // ─── HELPERS ──────────────────────────────────────────────────────────────
  const piecesOf = (m) => {
    const all   = m.pieces || [];
    const dispo = all.filter(p => p.inventaire_actif);
    return {
      ici:      dispo.filter(p => p.boutique_id === boutiqueId),
      ailleurs: dispo.filter(p => p.boutique_id !== boutiqueId),
      vendus:   all.filter(p => !p.inventaire_actif),
    };
  };

  const openSale = (piece, modele, commande = false) => {
    setIsCommande(commande);
    if (commande) {
      setSalePiece({ id_produit: null, reference_article: modele.reference_article, boutique_id: boutiqueId, modele });
    } else {
      setSalePiece({ ...piece, modele });
    }
    const prixSugg = commande
      ? (modele.pvp_magasin || modele.prix_vente || modele.prix_base || '')
      : (piece.pvp_magasin || modele.pvp_magasin || modele.prix_vente || modele.prix_base || '');
    setSaleForm(f => ({
      client_nom: selectedClient?.nom || f.client_nom || '',
      client_tel: selectedClient?.telephone || f.client_tel || '',
      prix: String(prixSugg), remise:'0', remise_montant:'', paiement:'especes', type_caisse:'officielle', obs:'',
      deduction_or_poids:'', deduction_or_prix_g:'', deduction_compte:'',
      paiement_or_poids:'', paiement_or_prix_g:'', mettre_en_compte: false, montant_paye_now:''
    }));
    setPaiements([]);
    setSaleSuccess(null);
    setSaleStep(1);
    setShowNewClientForm(false);
    // NE PAS effacer selectedClient — il a été choisi en phase 1
  };

  // Ouvre le modal "Pour quel client ?" au lieu de transférer directement
  const demanderTransfert = (piece, modele, type = 'reseau') => {
    if (!boutiqueId) { alert('Sélectionnez d\'abord votre boutique'); return; }
    setPendingTransfert({ piece, modele, type });
    setShowTransfertModal(true);
  };

  // Appelé par le modal après confirmation
  const confirmerTransfert = async ({ client, venduAvance }) => {
    if (!pendingTransfert) return;
    const { piece, modele, type } = pendingTransfert;
    setTransfertEnCours(true);
    try {
      const notes = type === 'commande'
        ? `Commande client : ${modele.reference_article}${client ? ` — pour ${client.nom}` : ''}`
        : `Demande transfert pièce ${piece?.id_produit || ''} (${modele.reference_article})${client ? ` — pour ${client.nom}` : ''}`;
      const body = {
        boutique_source_id: piece?.boutique_id || null,
        boutique_destination_id: boutiqueId,
        quantite: 1,
        notes,
        client_id: client?.id || null,
        vendu_avance: venduAvance,
      };
      const res = await fetch(`${API_URL}/transferts/demande-boutique`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Erreur'); return; }
      setShowTransfertModal(false);
      setPendingTransfert(null);
      // Affiche succès dans le modal
      return data;
    } catch (e) {
      alert('Erreur réseau : ' + e.message);
    } finally {
      setTransfertEnCours(false);
    }
  };

  const confirmSale = async () => {
    if (!salePiece) return;
    setSelling(true);
    try {
      // --- Prix et remise ---
      const prix        = parseFloat(saleForm.prix || 0);
      const remisePct   = parseFloat(saleForm.remise || 0);
      const remiseMnt   = parseFloat(saleForm.remise_montant || 0);
      const prixApresR  = remiseMnt > 0 ? prix - remiseMnt : prix * (1 - remisePct / 100);
      const sousTotal   = Math.max(0, prixApresR);

      // --- Paiements depuis le bloc REGLEMENT ---
      const paiementsPayload = [];
      if (parseFloat(saleForm.p_especes || 0) > 0) paiementsPayload.push({ mode: 'especes', montant: parseFloat(saleForm.p_especes) });
      if (parseFloat(saleForm.p_cheque || 0) > 0) paiementsPayload.push({ mode: 'cheque', montant: parseFloat(saleForm.p_cheque), reference: saleForm.p_cheque_ref || '' });
      if (parseFloat(saleForm.p_carte || 0) > 0) paiementsPayload.push({ mode: 'carte', montant: parseFloat(saleForm.p_carte) });
      if (parseFloat(saleForm.p_virement || 0) > 0) paiementsPayload.push({ mode: 'virement', montant: parseFloat(saleForm.p_virement) });
      const orPoids = parseFloat(saleForm.p_or_poids || 0);
      const orPrixG = parseFloat(saleForm.p_or_prix_g || 0);
      if (orPoids > 0 && orPrixG > 0) paiementsPayload.push({ mode: 'or', montant: orPoids * orPrixG, or_poids: orPoids, or_prix_g: orPrixG });

      const totalPaye = paiementsPayload.reduce((s, p) => s + p.montant, 0);
      const reste = Math.max(0, sousTotal - totalPaye);
      const tropPercu = Math.max(0, totalPaye - sousTotal);

      // Credit (pas assez payé → le reste va en compte client)
      if (reste > 0 && saleForm.mettre_en_compte && selectedClient) {
        paiementsPayload.push({ mode: 'credit', montant: -reste });
      }
      // Avoir (trop payé → crédité sur le compte client)
      if (tropPercu > 0 && saleForm.avoir_client && selectedClient) {
        paiementsPayload.push({ mode: 'avoir', montant: -tropPercu });
      }

      // Mode paiement pour backward compat
      const modesPositifs = paiementsPayload.filter(p => p.montant > 0);
      const modePaiementFinal = modesPositifs.length > 1 ? 'multiple'
        : modesPositifs.length === 1 ? modesPositifs[0].mode : 'especes';

      // montant_final = ce que le client doit vraiment (après remise, c'est le sous-total)
      const prixFinal = sousTotal;

      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      let res;
      const commonPayload = {
        client_id:     selectedClient?.id || undefined,
        client_nom:    saleForm.client_nom,
        client_tel:    saleForm.client_tel,
        prix_vente:    prix,
        remise_pourcentage: remisePct || undefined,
        remise_montant: remiseMnt > 0 ? remiseMnt : (prix - prixApresR > 0.01 ? round2(prix - prixApresR) : undefined),
        montant_final: prixFinal,
        mode_paiement: modePaiementFinal,
        type_caisse:   saleForm.type_caisse,
        boutique_id:   boutiqueId,
        observation:   saleForm.obs,
        paiements:     paiementsPayload.length > 0 ? paiementsPayload : undefined,
      };

      if (isCommande) {
        res = await fetch(`${API_URL}/bijoux/catalogue/commandes`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...commonPayload, reference_article: salePiece.reference_article, prix_final: prixFinal }),
        });
      } else {
        res = await fetch(`${API_URL}/bijoux/catalogue/pieces/${salePiece.id_produit}/vendre`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...commonPayload, remise: remisePct, prix_final: prixFinal }),
        });
      }

      const data = await res.json();
      if (!res.ok) {
        const msg = data.error || 'Erreur vente';
        notifyError(msg, { code: data.code, status: res.status });
        setSelling(false);
        return;
      }
      const refVente = data.reference_vente || data.numero_vente || data.id || '';
      notifySuccess(isCommande
        ? `Commande enregistree${refVente ? ' (' + refVente + ')' : ''}`
        : `Vente${refVente ? ' ' + refVente : ''} validee — ${prixFinal} DH`);
      // P2 caisse : vente possible caisse fermée, mais signalée (décision 3, 11/06/2026)
      if (data.warning === 'caisse_fermee') {
        notifyError('⚠ Vente enregistrée CAISSE FERMÉE — pensez à ouvrir la caisse du matin (fond de caisse)', { status: 200 });
      }
      setSaleSuccess({ ...data, prixFinal });

      if (!isCommande) {
        setModeles(prev => prev.map(m => {
          if (m.reference_article !== salePiece.reference_article) return m;
          return {
            ...m,
            nb_en_stock: Math.max(0, (m.nb_en_stock || 0) - 1),
            nb_vendus: (m.nb_vendus || 0) + 1,
            pieces: (m.pieces || []).map(p =>
              p.id_produit === salePiece.id_produit ? { ...p, inventaire_actif: 0 } : p
            ),
          };
        }));
        if (selectedModele?.reference_article === salePiece.reference_article) {
          setSelectedModele(prev => ({
            ...prev,
            pieces: (prev.pieces || []).map(p =>
              p.id_produit === salePiece.id_produit ? { ...p, inventaire_actif: 0 } : p
            ),
          }));
        }
      }
    } catch (e) { notifyError('Erreur reseau : ' + (e?.message || 'inconnue')); }
    setSelling(false);
  };

  // Groups de collections pour la vue d'accueil
  const collectionGroups = useMemo(() => {
    const GRAD_LIST = Object.values(TYPE_GRAD);
    const groups = {};
    modeles.forEach(m => {
      const key = m.collection_nom || '_aucune_';
      if (!groups[key]) groups[key] = {
        key, nom: m.collection_nom || 'Sans collection',
        nb_modeles: 0, stock_ici: 0, stock_reseau: 0,
        image_url: null, grad: null,
      };
      groups[key].nb_modeles++;
      (m.pieces || []).forEach(p => {
        if (!p.inventaire_actif) return;
        if (boutiqueId && p.boutique_id === boutiqueId) groups[key].stock_ici++;
        else groups[key].stock_reseau++;
      });
      if (!groups[key].image_url && m.image_url) groups[key].image_url = m.image_url;
      if (!groups[key].grad) groups[key].grad = TYPE_GRAD[(m.type_bijou || '').toUpperCase()] || GRAD_LIST[0];
    });
    return Object.values(groups).sort((a, b) => {
      if (a.key === '_aucune_') return 1;
      if (b.key === '_aucune_') return -1;
      return b.stock_ici + b.stock_reseau - (a.stock_ici + a.stock_reseau);
    });
  }, [modeles, boutiqueId]);

  const resetFilters = () => { setSearch(''); setFCol(''); setFType(''); setFDispo('tous'); };

  // Reset phase quand on change de modèle
  useEffect(() => { setPanelPhase(1); }, [selectedModele]);

  // ─── MODEL CARD ───────────────────────────────────────────────────────────
  const ModelCard = ({ m }) => {
    const { ici, ailleurs } = piecesOf(m);
    const grad  = TYPE_GRAD[(m.type_bijou || '').toUpperCase()] || 'from-gray-400 to-gray-600';
    const icon  = TYPE_ICON[(m.type_bijou || '').toUpperCase()]  || '💎';
    const prix  = m.pvp_magasin || m.prix_vente || m.prix_base;
    const hasIci     = ici.length > 0;
    const hasAilleurs = ailleurs.length > 0;

    return (
      <div
        onClick={() => setSelectedModele(m)}
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-xl hover:border-yellow-200 transition-all group select-none"
      >
        {/* Visual header */}
        <div className="h-40 relative flex items-center justify-center overflow-hidden" style={{ background: '#0f0f1a' }}>
          {m.image_url ? (
            <img
              src={m.image_url.startsWith('/') ? `${API_URL.replace("/api","")}${m.image_url}` : m.image_url}
              alt={m.nom_modele || m.reference_article}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
            />
          ) : null}
          <div className="flex flex-col items-center justify-center w-full h-full px-4" style={{ display: m.image_url ? 'none' : 'flex', background: 'linear-gradient(145deg, #0f0f1a 0%, #1a1a2e 55%, #16213e 100%)' }}>
            <div className="w-10 h-px mb-3" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)' }} />
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <Gem className="h-6 w-6" style={{ color: '#d4af37', opacity: 0.85 }} />
            </div>
            <div className="text-xs font-mono text-center w-full truncate" style={{ color: 'rgba(212,175,55,0.6)', letterSpacing: '0.08em' }}>{m.reference_article}</div>
            <div className="text-xs text-center w-full truncate mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{m.type_bijou}</div>
            <div className="w-10 h-px mt-3" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)' }} />
          </div>
          {/* Stock badges */}
          <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
            {hasIci && (
              <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-md">
                ✓ {ici.length} ici
              </span>
            )}
            {hasAilleurs && (
              <span className={`text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-md ${hasIci ? 'bg-blue-500' : 'bg-amber-500'}`}>
                {hasIci ? '+' : '◎ '}{ailleurs.length} réseau
              </span>
            )}
            {!hasIci && !hasAilleurs && (
              <span className="bg-gray-500 bg-opacity-80 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                Catalogue
              </span>
            )}
          </div>
          {/* Collection tag */}
          {m.collection_nom && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 px-3 py-1.5">
              <span className="text-white text-xs font-semibold opacity-90">{m.collection_nom}</span>
            </div>
          )}
        </div>

        {/* Info footer */}
        <div className="p-3">
          <div className="text-xs font-mono text-gray-400 mb-0.5 truncate">{m.reference_article}</div>
          <div className="font-semibold text-gray-800 text-sm leading-snug line-clamp-1 mb-2">
            {m.nom_modele || m.type_bijou || '—'}
          </div>
          {m.type_creation === 'DEPOT_VENTE' && (
            <div className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold inline-block mb-1">
              🤝 Dépôt{m.deposant ? ` — ${m.deposant}` : ''}
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{m.type_bijou}</span>
            {prix && (
              <span className="font-bold text-gray-900 text-sm">{fmt(prix)} DH</span>
            )}
          </div>
          {(m.pieces || []).length > 0 && (m.pieces[0].id_produit) && (
            <div className="text-xs text-gray-400 font-mono mt-1 truncate">
              {(m.pieces || []).length === 1
                ? m.pieces[0].id_produit
                : `${m.pieces[0].id_produit}${m.pieces.length > 1 ? ` +${m.pieces.length - 1}` : ''}`
              }
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── PIECE ROW (inside model panel) ───────────────────────────────────────
  const PieceRow = ({ piece, isLocal, modele }) => (
    <div className={`px-4 py-3 rounded-xl border transition-all ${
      isLocal ? 'bg-green-50 border-green-200 hover:bg-green-100' : 'bg-white border-gray-200 hover:border-amber-300'
    }`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {piece.reference && (
              <span className="font-mono text-xs font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">{piece.reference}</span>
            )}
            <span className="font-mono text-xs text-gray-400">{piece.id_produit}</span>
            {piece.couleur_or && (
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                piece.couleur_or === 'BLANC' ? 'bg-gray-100 text-gray-600' :
                piece.couleur_or === 'ROSE' ? 'bg-pink-100 text-pink-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>Or {piece.couleur_or.toLowerCase()}</span>
            )}
            {piece.statut_fabrication && piece.statut_fabrication !== 'termine' && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{piece.statut_fabrication}</span>
            )}
            {piece.type_creation === 'DEPOT_VENTE' && (
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">
                🤝 Dépôt{modele?.deposant ? ` — ${modele.deposant}` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {piece.poids_metal && (
              <span className="text-gray-600 font-mono font-semibold">⚖ {parseFloat(piece.poids_metal).toFixed(2)}g</span>
            )}
            {piece.poids_pierres_carats > 0 && (
              <span className="text-indigo-600 font-semibold">💎 {parseFloat(piece.poids_pierres_carats).toFixed(2)}ct</span>
            )}
            {piece.rhodiage ? <span className="text-gray-400">Rhodié</span> : null}
            {piece.certificat ? <span className="text-blue-500">Cert.</span> : null}
          </div>
          {piece.pierres_desc && (
            <div className="mt-1.5 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-lg leading-snug">
              {piece.pierres_desc}
              {piece.total_carats ? <span className="text-indigo-400 ml-1">({parseFloat(piece.total_carats).toFixed(2)} ct)</span> : null}
            </div>
          )}
          {piece.nom && piece.nom.toUpperCase() !== (piece.reference_article || '').toUpperCase() && (
            <div className="text-xs text-gray-400 mt-1 truncate">{piece.nom}</div>
          )}
        </div>
        <div className="text-right shrink-0 space-y-1.5">
          {piece.pvp_magasin && (
            <div className="text-xs font-semibold text-gray-600 mb-1">{fmt(piece.pvp_magasin)} DH</div>
          )}
          {/* Dû déposant : champ strippé serveur pour la vendeuse -> visible central/admin seulement */}
          {piece.prix_net_deposant != null && (
            <div className="text-xs font-semibold text-amber-700">dû déposant {fmt(piece.prix_net_deposant)} DH</div>
          )}
          {piece.du_a_saisir ? (
            <span className="block w-full px-3 py-2.5 rounded-lg text-xs font-bold bg-gray-100 text-gray-400 text-center whitespace-nowrap cursor-not-allowed"
              title="Dû déposant non saisi — à renseigner via Dépôts → Éditer prix">
              dû à renseigner d'abord
            </span>
          ) : (
          <button
            onClick={() => openSale(piece, modele)}
            className={`w-full px-3 py-2.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap ${
              isLocal
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
          >
            {isLocal ? 'Vendre' : 'Vendre ↗'}
          </button>
          )}
          {!isLocal && boutiqueId && (
            <button
              onClick={() => demanderTransfert(piece, modele)}
              disabled={transfertEnCours}
              className="w-full px-3 py-2.5 rounded-lg text-xs font-semibold border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors whitespace-nowrap flex items-center justify-center gap-1"
            >
              <Truck className="h-3 w-3" />
              Transfert
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ─── MODEL DETAIL PANEL — 2 phases ──────────────────────────────────────────
  const ModelPanel = ({ m }) => {
    const { ici, ailleurs, vendus } = piecesOf(m);
    const grad = TYPE_GRAD[(m.type_bijou || '').toUpperCase()] || 'from-gray-500 to-gray-700';
    const icon = TYPE_ICON[(m.type_bijou || '').toUpperCase()] || '💎';
    const pvp  = m.pvp_magasin || m.prix_vente || m.prix_base;

    // Composition depuis la première pièce disponible
    const firstP     = [...ici, ...ailleurs].find(p => p.poids_metal || p.pierres_desc) || [...ici, ...ailleurs][0];
    const couleur    = firstP?.couleur_or || '';
    const poidsMetal = firstP?.poids_metal;
    const poidsBrut  = firstP?.poids_brut;
    const pierresDesc   = firstP?.pierres_desc;
    const totalPierres  = firstP?.total_pierres;
    const totalCarats   = firstP?.total_carats;

    const couleurLabel = (c) => {
      const map = { BLANC: 'Or Blanc 18k', JAUNE: 'Or Jaune 18k', ROSE: 'Or Rose 18k', GRIS: 'Or Gris 18k' };
      return map[(c || '').toUpperCase()] || (c ? `Or ${c}` : '');
    };

    const groupedAilleurs = {};
    ailleurs.forEach(p => {
      const k = String(p.boutique_id || 'nc');
      if (!groupedAilleurs[k]) groupedAilleurs[k] = { nom: p.boutique_nom || 'Autre boutique', pieces: [] };
      groupedAilleurs[k].pieces.push(p);
    });

    return (
      <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-stretch justify-end" onClick={() => setSelectedModele(null)}>
        <div className="bg-white w-full sm:max-w-xl max-h-[95vh] sm:max-h-full sm:h-full flex flex-col shadow-2xl rounded-t-2xl sm:rounded-none" onClick={e => e.stopPropagation()}>

          {/* ══════════════ PHASE 1 — FICHE PRODUIT ══════════════ */}
          {panelPhase === 1 && (
            <>
              <div className="flex-1 overflow-y-auto">

                {/* ── Hero visuel ── */}
                <div className="relative">
                  {m.image_url ? (
                    <img
                      src={m.image_url.startsWith('/') ? `${API_URL.replace("/api","")}${m.image_url}` : m.image_url}
                      alt={m.nom_modele}
                      className="w-full h-80 object-cover"
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-80 bg-gradient-to-br ${grad} flex items-center justify-center`}
                    style={{ display: m.image_url ? 'none' : 'flex' }}
                  >
                    <span className="text-[7rem] drop-shadow-2xl select-none">{icon}</span>
                  </div>
                  {/* Bouton fermer */}
                  <button
                    onClick={() => setSelectedModele(null)}
                    className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 backdrop-blur-sm transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  {/* Overlay bas */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent px-6 pb-5 pt-16">
                    <div className="font-mono text-white/50 text-xs mb-1">
                      {m.reference_article}
                      {firstP?.id_produit && (
                        <span className="ml-2 text-white/35">· {firstP.id_produit}</span>
                      )}
                    </div>
                    <div className="text-white font-bold text-2xl leading-tight">{m.nom_modele || m.type_bijou}</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {m.type_bijou && (
                        <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full backdrop-blur-sm">{m.type_bijou}</span>
                      )}
                      {m.collection_nom && (
                        <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full backdrop-blur-sm">✦ {m.collection_nom}</span>
                      )}
                      {couleur && (
                        <span className="text-xs bg-yellow-500/80 text-white px-3 py-1 rounded-full backdrop-blur-sm font-semibold">{couleurLabel(couleur)}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Prix ── */}
                <div className="px-6 py-5 border-b border-gray-100">
                  {pvp ? (
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-1">Prix de vente conseillé</div>
                        <div className="text-4xl font-bold text-gray-900 tracking-tight">
                          {fmt(pvp)}
                          <span className="text-xl font-normal text-gray-400 ml-2">DH</span>
                        </div>
                        {m.pvp_magasin && m.prix_vente && parseFloat(m.pvp_magasin) !== parseFloat(m.prix_vente) && (
                          <div className="text-xs text-gray-400 mt-1">Prix catalogue : {fmt(m.prix_vente)} DH</div>
                        )}
                      </div>
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center text-3xl shadow-lg`}>
                        {icon}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 italic text-sm">Prix non renseigné</div>
                  )}
                </div>

                {/* ── Composition Or ── */}
                {(couleur || poidsMetal) && (
                  <div className="px-6 py-5 border-b border-gray-100">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Or &amp; Métal</div>
                    <div className="flex items-center gap-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl px-4 py-3.5">
                      <div className="w-11 h-11 bg-yellow-100 rounded-xl flex items-center justify-center text-2xl shrink-0">🥇</div>
                      <div className="flex-1">
                        <div className="font-bold text-yellow-900 text-base">{couleurLabel(couleur) || 'Or 18k'}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-1.5 text-sm">
                          {poidsMetal && <div className="text-yellow-700"><span className="font-semibold">{parseFloat(poidsMetal).toFixed(2)}g</span> métal net</div>}
                          {poidsBrut && <div className="text-yellow-600/70">{parseFloat(poidsBrut).toFixed(2)}g brut</div>}
                          {firstP?.poids_metal_travaille && <div className="text-yellow-600/70">{parseFloat(firstP.poids_metal_travaille).toFixed(2)}g travaillé</div>}
                          {firstP?.poids_chaine && <div className="text-yellow-600/70">{parseFloat(firstP.poids_chaine).toFixed(2)}g chaîne</div>}
                          {firstP?.poids_mousqueton && <div className="text-yellow-600/70">{parseFloat(firstP.poids_mousqueton).toFixed(2)}g mousqueton</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Pierres ── */}
                {pierresDesc && (
                  <div className="px-6 py-5 border-b border-gray-100">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Pierres</div>
                    <div className="flex items-start gap-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl px-4 py-3.5">
                      <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center text-2xl shrink-0">💎</div>
                      <div className="flex-1">
                        <div className="font-bold text-indigo-900 text-base">
                          {totalPierres ? `${totalPierres} pierre${totalPierres > 1 ? 's' : ''}` : 'Pierres'}
                          {totalCarats ? <span className="text-indigo-500 font-normal text-sm ml-2">· {parseFloat(totalCarats).toFixed(2)} ct</span> : null}
                        </div>
                        <div className="text-sm text-indigo-700 mt-0.5 leading-snug">{pierresDesc}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Options fabrication ── */}
                {firstP && (firstP.rhodiage || firstP.gravure_beldi || firstP.fonte2 || firstP.garantie || firstP.ecrin || firstP.certificat) && (
                  <div className="px-6 py-5 border-b border-gray-100">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Détails fabrication</div>
                    <div className="flex flex-wrap gap-2">
                      {firstP.rhodiage ? <span className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg font-medium">Rhodiage</span> : null}
                      {firstP.gravure_beldi ? <span className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg font-medium">Gravure beldi</span> : null}
                      {firstP.fonte2 ? <span className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg font-medium">Fonte</span> : null}
                      {firstP.garantie ? <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1.5 rounded-lg font-medium">Garantie</span> : null}
                      {firstP.ecrin ? <span className="text-xs bg-purple-100 text-purple-700 px-2.5 py-1.5 rounded-lg font-medium">Écrin</span> : null}
                      {firstP.certificat ? <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1.5 rounded-lg font-medium">Certificat : {firstP.certificat}</span> : null}
                      {firstP.dimensions ? <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1.5 rounded-lg">{firstP.dimensions}</span> : null}
                    </div>
                    {firstP.date_fabrication && (
                      <div className="text-xs text-gray-400 mt-2">Fabriqué le {new Date(firstP.date_fabrication).toLocaleDateString('fr-FR')}</div>
                    )}
                  </div>
                )}

                {/* ── Description ── */}
                {m.description && m.description !== m.reference_article && (
                  <div className="px-6 py-5 border-b border-gray-100">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Description</div>
                    <p className="text-sm text-gray-600 leading-relaxed">{m.description}</p>
                  </div>
                )}

                {/* ── Étapes de fabrication (publiques — sans ouvriers ni prix) ── */}
                {(() => {
                  const piece = (m.pieces || [])[0];
                  const etapes = piece?.etapes_production || m.etapes_production || [];
                  if (!etapes || etapes.length === 0) return null;
                  const etapesTerminees = etapes.filter(e => e.statut === 'terminee' || e.statut === 'validee');
                  if (etapesTerminees.length === 0) return null;
                  return (
                    <div className="px-6 py-5 border-b border-gray-100">
                      <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Étapes de fabrication</div>
                      <div className="flex flex-wrap gap-2">
                        {etapesTerminees.map((e, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1.5 rounded-lg font-medium">
                            <span className="w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                            {e.nom_etape}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Stats + stock par boutique ── */}
                <div className="px-6 py-5">
                  <div className="flex gap-6 text-sm text-gray-400 mb-3">
                    <span><span className="font-bold text-gray-700">{m.nb_pieces_total || 0}</span> fabriquée(s)</span>
                    <span><span className="font-bold text-green-600">{m.nb_en_stock || 0}</span> dispo</span>
                    <span><span className="font-bold text-gray-500">{m.nb_vendus || 0}</span> vendue(s)</span>
                  </div>
                  {m.boutiques_stock && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>Stock : {m.boutiques_stock}</span>
                    </div>
                  )}
                </div>

                <div className="h-28" />
              </div>

              {/* ── CTA sticky bas ── */}
              <div className="shrink-0 border-t border-gray-100 bg-white px-6 py-4 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-gray-500">
                    {ici.length > 0
                      ? <><span className="font-bold text-green-600">{ici.length}</span> pièce(s) disponible(s) ici</>
                      : ailleurs.length > 0
                        ? <><span className="font-bold text-amber-600">{ailleurs.length}</span> pièce(s) dans le réseau</>
                        : <span className="text-violet-600 font-semibold">Disponible sur commande</span>
                    }
                  </div>
                  {pvp && <div className="font-bold text-gray-800 text-base">{fmt(pvp)} DH</div>}
                </div>
                <button
                  onClick={() => setPanelPhase(2)}
                  className={`w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] ${
                    ici.length > 0
                      ? 'bg-gray-900 hover:bg-gray-800 text-white'
                      : ailleurs.length > 0
                        ? 'bg-amber-600 hover:bg-amber-700 text-white'
                        : 'bg-violet-600 hover:bg-violet-700 text-white'
                  }`}
                >
                  <ShoppingBag className="h-5 w-5" />
                  {ici.length > 0
                    ? 'Voir les disponibilités & Vendre'
                    : ailleurs.length > 0
                      ? 'Voir le stock réseau & Vendre'
                      : 'Vendre sur commande'
                  }
                </button>
              </div>
            </>
          )}

          {/* ══════════════ PHASE 2 — DISPONIBILITÉS & VENTE ══════════════ */}
          {panelPhase === 2 && (
            <>
              {/* Header mini */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
                <button
                  onClick={() => setPanelPhase(1)}
                  className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 text-gray-700" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate">{m.nom_modele || m.type_bijou}</div>
                  <div className="font-mono text-xs text-gray-400">{m.reference_article}</div>
                </div>
                <button onClick={() => setSelectedModele(null)} className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="h-4 w-4 text-gray-700" />
                </button>
              </div>

              {/* Stock summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 px-4 py-3 bg-gray-50 shrink-0">
                <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-green-100">
                  <div className="text-2xl font-bold text-green-600">{ici.length}</div>
                  <div className="text-xs text-gray-500 mt-0.5">En stock ici</div>
                </div>
                <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-amber-100">
                  <div className="text-2xl font-bold text-amber-600">{ailleurs.length}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Réseau</div>
                </div>
                <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
                  <div className="text-2xl font-bold text-gray-400">{vendus.length}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Vendus</div>
                </div>
              </div>

              {/* Liste des pièces */}
              <div className="flex-1 overflow-y-auto p-4 space-y-5">

                {ici.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                      <span className="text-sm font-bold text-gray-700">📍 {boutique?.nom || 'Votre boutique'}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">{ici.length} dispo</span>
                    </div>
                    <div className="space-y-2">
                      {ici.map(p => <PieceRow key={p.id_produit} piece={p} isLocal={true} modele={m} />)}
                    </div>
                  </div>
                )}

                {Object.entries(groupedAilleurs).map(([bid, { nom, pieces }]) => (
                  <div key={bid}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                      <span className="text-sm font-bold text-gray-700">📍 {nom}</span>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{pieces.length} dispo</span>
                    </div>
                    <div className="space-y-2">
                      {pieces.map(p => <PieceRow key={p.id_produit} piece={p} isLocal={false} modele={m} />)}
                    </div>
                  </div>
                ))}

                {/* Aucune pièce → choix transfert ou commande */}
                {ici.length === 0 && ailleurs.length === 0 && (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="h-8 w-8 text-gray-300" />
                    </div>
                    <p className="font-semibold text-gray-600 mb-1">Aucune pièce en stock</p>
                    <p className="text-xs text-gray-400 mb-5">Ni ici ni dans le réseau</p>

                    {/* Option 1 : Demande transfert depuis l'atelier/central */}
                    {boutiqueId && (
                      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-3 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="h-4 w-4 text-blue-600" />
                          <div className="text-sm font-bold text-blue-800">Demander au central / atelier</div>
                        </div>
                        <p className="text-xs text-blue-600 mb-3">La pièce sera fabriquée ou transférée depuis le stock central pour ce client.</p>
                        <button
                          onClick={() => demanderTransfert(null, m, 'commande')}
                          disabled={transfertEnCours}
                          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Truck className="h-4 w-4" />
                          Demander pour un client
                        </button>
                      </div>
                    )}

                    {/* Option 2 : Vente sur commande (avec paiement immédiat) */}
                    <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <ShoppingBag className="h-4 w-4 text-violet-600" />
                        <div className="text-sm font-bold text-violet-800">Vente sur commande</div>
                      </div>
                      <p className="text-xs text-violet-600 mb-3">Encaisser le client maintenant, la pièce lui sera remise dès disponibilité.</p>
                      <button
                        onClick={() => openSale(null, m, true)}
                        className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                      >
                        <ShoppingBag className="h-4 w-4" />
                        Vendre sur commande
                      </button>
                    </div>
                  </div>
                )}

                {vendus.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="text-xs text-gray-400 text-center">{vendus.length} pièce(s) déjà vendue(s)</div>
                  </div>
                )}

                {/* Toujours proposer la commande même s'il y a du stock */}
                {(ici.length > 0 || ailleurs.length > 0) && (
                  <div className="border-t pt-4">
                    <button
                      onClick={() => openSale(null, m, true)}
                      className="w-full py-2.5 border border-violet-200 text-violet-700 hover:bg-violet-50 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <Package className="h-4 w-4" />
                      Vendre une pièce supplémentaire sur commande
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    );
  };

  // ─── SALE MODAL ───────────────────────────────────────────────────────────
  const SaleModal = ({ piece }) => {
    const isLocal   = !isCommande && piece.boutique_id === boutiqueId;
    const prixV     = parseFloat(saleForm.prix   || 0);
    const remiseP   = parseFloat(saleForm.remise || 0);
    const remiseM   = parseFloat(saleForm.remise_montant || 0);
    const prixApresRemise = remiseM > 0 ? prixV - remiseM : prixV * (1 - remiseP / 100);
    // Sous-total = prix apres remise. La repartition se fait dans le bloc REGLEMENT.
    const sousTotal = Math.max(0, prixApresRemise);
    const pvpRef    = parseFloat(piece.modele?.pvp_magasin || piece.modele?.prix_vente || 0);
    const overPvp   = pvpRef > 0 && sousTotal > pvpRef * 1.001;

    // SUCCESS STATE
    if (saleSuccess) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-sm w-full p-6 sm:p-8 text-center max-h-[95vh] overflow-y-auto">
            <div className={`w-20 h-20 ${isCommande ? 'bg-violet-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-5`}>
              <CheckCircle className={`h-12 w-12 ${isCommande ? 'text-violet-500' : 'text-green-500'}`} />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">
              {isCommande ? 'Commande enregistrée !' : 'Vente enregistrée !'}
            </h3>
            <p className="text-gray-500 mb-5 font-mono text-sm">
              {isCommande ? piece.reference_article : piece.id_produit}
            </p>
            <div className={`bg-gradient-to-r ${isCommande ? 'from-violet-50 to-purple-50 border-violet-100' : 'from-green-50 to-emerald-50 border-green-100'} rounded-2xl p-5 mb-6 border`}>
              <div className={`text-4xl font-bold ${isCommande ? 'text-violet-600' : 'text-green-600'} mb-1`}>{fmt(saleSuccess.prixFinal)} DH</div>
              {saleForm.client_nom && <div className="text-sm text-gray-500">Client : {saleForm.client_nom}</div>}
              {saleSuccess.num_facture && (
                <div className="text-xs text-gray-400 mt-1">🧾 {saleSuccess.num_facture}</div>
              )}
              {isCommande && (
                <div className="text-xs text-violet-600 mt-2 font-medium">
                  📦 La pièce sera remise au client dès disponibilité
                </div>
              )}
            </div>
            <div className="flex gap-2 mb-3">
              {!isCommande && (
                <button
                  onClick={async () => {
                    const { generateFacturePDF } = await import('../../utils/pdfGenerator');
                    generateFacturePDF({
                      id: saleSuccess.vente_id || Date.now(),
                      date_vente: new Date().toISOString(),
                      client_nom: saleForm.client_nom || 'Client',
                      client_telephone: saleForm.client_tel || '',
                      boutique_nom: boutique?.nom || 'Boutique',
                      bijou_nom: piece.modele?.nom_modele || piece.nom || piece.reference_article || '',
                      reference: piece.reference_article || piece.id_produit || '',
                      categorie: piece.modele?.type_bijou || '',
                      prix_vente: saleSuccess.prixFinal,
                      mode_paiement: paiements.length > 1 ? 'multiple' : paiements.length === 1 ? paiements[0].mode : saleForm.paiement,
                    });
                  }}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5 text-sm"
                >
                  <FileText className="h-4 w-4" />
                  Facture PDF
                </button>
              )}
              <button
                onClick={async () => {
                  const { generateEtiquetteBijouPDF } = await import('../../utils/pdfGenerator');
                  generateEtiquetteBijouPDF(piece);
                }}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-1.5 text-sm"
              >
                <Tag className="h-4 w-4" />
                Etiquette
              </button>
            </div>
            <button
              onClick={() => { setSalePiece(null); setSaleSuccess(null); setIsCommande(false); setSelectedClient(null); }}
              className="w-full py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end sm:items-center justify-center sm:p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className={`bg-gradient-to-r ${isCommande ? 'from-violet-700 to-purple-800' : 'from-gray-800 to-gray-900'} text-white px-6 py-4 rounded-t-2xl sm:rounded-t-2xl flex justify-between items-start`}>
            <div>
              <div className="font-bold text-lg">
                {isCommande ? '📦 Vente sur commande' : 'Nouvelle Vente'}
              </div>
              <div className="text-white/50 text-xs font-mono mt-0.5">
                {isCommande ? piece.reference_article : piece.id_produit}
              </div>
            </div>
            <button onClick={() => { setSalePiece(null); setIsCommande(false); setSelectedClient(null); }} className="hover:bg-white/10 rounded-lg p-1.5 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {/* Piece summary */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${TYPE_GRAD[(piece.modele?.type_bijou || '').toUpperCase()] || 'from-gray-400 to-gray-600'} flex items-center justify-center text-2xl shrink-0`}>
                  {TYPE_ICON[(piece.modele?.type_bijou || '').toUpperCase()] || '💎'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-gray-800 text-sm truncate">
                    {piece.modele?.nom_modele || piece.modele?.reference_article || piece.reference_article}
                  </div>
                  <div className="font-mono text-xs text-gray-400 mt-0.5">{piece.id_produit}</div>
                  {!isLocal && (
                    <div className="text-xs text-amber-600 font-medium mt-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      En stock à: {piece.boutique_nom || 'autre boutique'}
                    </div>
                  )}
                </div>
              </div>
              {/* Composition details */}
              <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-200">
                {piece.couleur_or && (
                  <span className="inline-flex items-center gap-1 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                    🥇 Or {piece.couleur_or}
                  </span>
                )}
                {piece.poids_metal && (
                  <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full font-mono">
                    ⚖️ {parseFloat(piece.poids_metal).toFixed(2)} g métal
                  </span>
                )}
                {piece.pierres_desc && (
                  <span className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                    💎 {piece.pierres_desc}
                    {piece.total_carats ? <span className="text-indigo-400 ml-1">· {parseFloat(piece.total_carats).toFixed(2)} ct</span> : null}
                  </span>
                )}
                {!piece.couleur_or && !piece.poids_metal && !piece.pierres_desc && (
                  <span className="text-xs text-gray-400 italic">Composition non renseignée</span>
                )}
              </div>
            </div>

            {/* Client — masquer la recherche si déjà sélectionné */}
            {selectedClient ? (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 mb-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-blue-900 truncate">{selectedClient.nom}</div>
                  <div className="text-xs text-blue-500">{selectedClient.telephone || ''}</div>
                </div>
                {parseFloat(selectedClient.solde_or_grammes || 0) > 0 && (
                  <span className="text-[11px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-medium">Or: {parseFloat(selectedClient.solde_or_grammes).toFixed(1)}g</span>
                )}
              </div>
            ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Client</label>
                <span className="text-xs text-gray-400 italic">Optionnel</span>
              </div>

              {/* Client sélectionné — mini carte */}
              {saleForm.client_nom && (
                <div className="mb-2 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-blue-900 truncate">{saleForm.client_nom}</div>
                    <div className="text-xs text-blue-500">{saleForm.client_tel}</div>
                    {selectedClient && (parseFloat(selectedClient.solde_or_grammes || 0) > 0 || parseFloat(selectedClient.solde_compte || 0) > 0) && (
                      <div className="flex gap-2 mt-1">
                        {parseFloat(selectedClient.solde_or_grammes || 0) > 0 && (
                          <span className="text-[11px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-medium">Or: {parseFloat(selectedClient.solde_or_grammes).toFixed(3)}g</span>
                        )}
                        {parseFloat(selectedClient.solde_compte || 0) > 0 && (
                          <span className="text-[11px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-medium">Compte: {fmt(selectedClient.solde_compte)} DH</span>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => { setSaleForm(f => ({ ...f, client_nom: '', client_tel: '', deduction_or_poids:'', deduction_or_prix_g:'', deduction_compte:'' })); setShowClientSugg(false); setSelectedClient(null); }}
                    className="text-blue-300 hover:text-red-400 transition-colors shrink-0">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Champ recherche client */}
              <div className="relative mb-2">
                <div className="flex items-center gap-2 px-3 py-3 border border-gray-200 rounded-xl bg-white focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-50 transition-all">
                  <Search className="h-4 w-4 text-gray-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Nom, prénom ou N° de téléphone…"
                    value={saleForm.client_nom}
                    onChange={e => {
                      setSaleForm(f => ({ ...f, client_nom: e.target.value }));
                      searchClients(e.target.value);
                    }}
                    onBlur={() => setTimeout(() => setShowClientSugg(false), 400)}
                    onFocus={() => saleForm.client_nom.length >= 2 && searchClients(saleForm.client_nom)}
                    className="flex-1 outline-none text-sm bg-transparent"
                  />
                  {saleForm.client_nom && (
                    <button onClick={() => { setSaleForm(f => ({ ...f, client_nom: '', client_tel: '', deduction_or_poids:'', deduction_or_prix_g:'', deduction_compte:'' })); setShowClientSugg(false); setSelectedClient(null); }}
                      className="text-gray-300 hover:text-red-400 transition-colors shrink-0">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mt-1 px-1">
                  <span className="text-xs text-gray-400">Ex : <span className="font-mono">Amina</span>, <span className="font-mono">Benkirane</span> ou <span className="font-mono">0661…</span></span>
                </div>
                {/* Suggestions dropdown */}
                {showClientSugg && clientSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
                    <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                      <span className="text-xs font-semibold text-blue-700">Clients trouvés</span>
                      <span className="text-xs text-blue-400">{clientSuggestions.length} résultat{clientSuggestions.length > 1 ? 's' : ''}</span>
                    </div>
                    {clientSuggestions.map(c => {
                      const tel = c.telephone || c.tel || '';
                      const isPhoneMatch = tel && saleForm.client_nom && tel.includes(saleForm.client_nom.replace(/\s/g, ''));
                      return (
                        <button key={c.id} onMouseDown={() => selectClient(c)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 active:bg-blue-100 transition-colors border-b border-gray-50 last:border-0 flex items-center gap-3">
                          <div className="w-9 h-9 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-sm font-bold text-blue-700">
                              {(c.nom || '').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">{c.nom}</div>
                            {tel && (
                              <div className={`text-xs flex items-center gap-1 ${isPhoneMatch ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                                <Phone className="h-3 w-3" />
                                {tel}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-right shrink-0 space-y-0.5">
                            {c.nb_achats > 0 && (
                              <div className="font-semibold text-blue-600">{c.nb_achats} achat{c.nb_achats > 1 ? 's' : ''}</div>
                            )}
                            {parseFloat(c.total_achats) > 0 && (
                              <div className="text-gray-400">{fmt(c.total_achats)} DH</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    <div className="px-4 py-2 bg-gray-50 text-xs text-gray-400 text-center border-t border-gray-100">
                      Sélectionner · ou continuer à taper pour saisir un nouveau client
                    </div>
                  </div>
                )}
              </div>

              {/* Téléphone */}
              <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-xl bg-white focus-within:border-blue-400 transition-colors">
                <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                  type="tel" placeholder="Téléphone"
                  value={saleForm.client_tel}
                  onChange={e => setSaleForm(f => ({ ...f, client_tel: e.target.value }))}
                  className="flex-1 outline-none text-sm bg-transparent"
                />
              </div>

              {/* Bouton nouveau client */}
              {!showNewClientForm ? (
                <button
                  onClick={() => setShowNewClientForm(true)}
                  className="w-full mt-1 py-2 text-xs font-semibold text-green-700 border border-dashed border-green-300 rounded-xl bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-center gap-1.5"
                >
                  <span className="text-base leading-none">+</span> Nouveau client (pas encore enregistré)
                </button>
              ) : (
                <div className="mt-1 rounded-xl border border-green-300 bg-green-50 p-3 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-green-800">Créer un nouveau client</span>
                    <button onClick={() => { setShowNewClientForm(false); setNewClientInSale({ nom: '', telephone: '' }); }}
                      className="text-green-400 hover:text-red-400 transition-colors"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <input
                    type="text" placeholder="Nom complet *" autoComplete="off"
                    value={newClientInSale.nom}
                    onChange={e => setNewClientInSale(n => ({ ...n, nom: e.target.value }))}
                    className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm bg-white outline-none focus:border-green-500"
                  />
                  <input
                    type="tel" placeholder="Téléphone" autoComplete="off"
                    value={newClientInSale.telephone}
                    onChange={e => setNewClientInSale(n => ({ ...n, telephone: e.target.value }))}
                    className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm bg-white outline-none focus:border-green-500"
                  />
                  <button
                    onClick={createClientInSale}
                    disabled={savingNewClient || !newClientInSale.nom.trim()}
                    className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    {savingNewClient ? 'Création...' : 'Créer et sélectionner'}
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Prix de vente */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Prix de vente</label>
                {pvpRef > 0 && (
                  <span className="text-xs text-gray-500">
                    PVP conseillé : <span className="font-bold text-gray-700">{fmt(pvpRef)} DH</span>
                  </span>
                )}
              </div>
              <input
                type="text" inputMode="decimal" onFocus={e => e.target.select()} min="0" step="0.01"
                value={saleForm.prix}
                onChange={e => setSaleForm(f => ({ ...f, prix: e.target.value }))}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm font-bold outline-none transition-colors ${
                  overPvp ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-gray-200 focus:border-blue-400'
                }`}
                placeholder="Prix de vente (DH)"
              />
            </div>

            {/* Remise */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
              <div className="text-xs font-bold text-orange-800 uppercase tracking-wider">Remise</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Remise (%)</div>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()} min="0" max="100"
                    value={saleForm.remise}
                    onChange={e => {
                      const pct = e.target.value;
                      const mnt = prixV > 0 && pct ? (prixV * parseFloat(pct || 0) / 100).toFixed(2) : '';
                      setSaleForm(f => ({ ...f, remise: pct, remise_montant: mnt }));
                    }}
                    className="w-full px-3 py-2.5 border border-orange-200 rounded-xl text-sm outline-none focus:border-orange-400 bg-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Montant remise (DH)</div>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()} min="0"
                    value={saleForm.remise_montant}
                    onChange={e => {
                      const mnt = e.target.value;
                      const pct = prixV > 0 && mnt ? ((parseFloat(mnt || 0) / prixV) * 100).toFixed(2) : '';
                      setSaleForm(f => ({ ...f, remise_montant: mnt, remise: pct }));
                    }}
                    className="w-full px-3 py-2.5 border border-orange-200 rounded-xl text-sm outline-none focus:border-orange-400 bg-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
              {prixApresRemise < prixV && (
                <div className="text-sm font-semibold text-orange-700">
                  Prix apres remise : {fmt(prixApresRemise)} DH
                </div>
              )}
            </div>

            {/* Prix après remise (si remise appliquée) */}
            {prixApresRemise < prixV && (
              <div className="flex justify-between items-center bg-gray-100 rounded-xl px-4 py-2">
                <span className="text-sm text-gray-600">Prix apres remise</span>
                <span className="text-lg font-bold text-gray-800">{fmt(prixApresRemise)} DH</span>
              </div>
            )}

            {/* Type de caisse — §201 admin only (vendeuse : masqué, défaut 'officielle') */}
            {showTypeCaisse && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type de caisse</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSaleForm(f => ({ ...f, type_caisse: 'officielle' }))}
                  className={`py-3.5 rounded-xl border text-center transition-all ${
                    saleForm.type_caisse === 'officielle'
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="text-lg leading-none">🧾</div>
                  <div className="text-xs font-semibold mt-1">Officielle</div>
                  <div className="text-[10px] opacity-70 mt-0.5">Déclarée, avec facture</div>
                </button>
                <button
                  onClick={() => setSaleForm(f => ({ ...f, type_caisse: 'instance' }))}
                  className={`py-3.5 rounded-xl border text-center transition-all ${
                    saleForm.type_caisse === 'instance'
                      ? 'bg-orange-500 text-white border-orange-500 shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className="text-lg leading-none">⏳</div>
                  <div className="text-xs font-semibold mt-1">En instance</div>
                  <div className="text-[10px] opacity-70 mt-0.5">Non déclarée pour l'instant</div>
                </button>
              </div>
            </div>
            )}

            {/* ═══ REGLEMENT — style bijouterie ═══ */}
            {(() => {
              // Calculs temps réel
              const especes = parseFloat(saleForm.p_especes || 0);
              const cheque = parseFloat(saleForm.p_cheque || 0);
              const carte = parseFloat(saleForm.p_carte || 0);
              const virement = parseFloat(saleForm.p_virement || 0);
              const orPoids = parseFloat(saleForm.p_or_poids || 0);
              const orPrixG = parseFloat(saleForm.p_or_prix_g || 0);
              const orMontant = orPoids * orPrixG;
              const totalPaye = especes + cheque + carte + virement + orMontant;
              const reste = Math.max(0, sousTotal - totalPaye);
              const monnaie = totalPaye > sousTotal ? totalPaye - sousTotal : 0;

              return (
                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  {/* En-tete */}
                  <div className="bg-gray-800 text-white px-4 py-3 flex justify-between items-center">
                    <span className="text-sm font-bold uppercase tracking-wide">Reglement</span>
                    <span className="text-lg font-bold">{fmt(sousTotal)} DH</span>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Especes */}
                    <div className="flex items-center gap-3">
                      <div className="w-24 text-sm font-semibold text-gray-700">Especes</div>
                      <input type="text" inputMode="decimal" value={saleForm.p_especes || ''} onFocus={e => e.target.select()}
                        onChange={e => setSaleForm(f => ({...f, p_especes: e.target.value}))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-300 focus:outline-none" placeholder="0.00" />
                      <span className="text-xs text-gray-400 w-8">DH</span>
                    </div>

                    {/* Cheque */}
                    <div className="flex items-center gap-3">
                      <div className="w-24 text-sm font-semibold text-gray-700">Cheque</div>
                      <input type="text" inputMode="decimal" value={saleForm.p_cheque || ''} onFocus={e => e.target.select()}
                        onChange={e => setSaleForm(f => ({...f, p_cheque: e.target.value}))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-300 focus:outline-none" placeholder="0.00" />
                      <span className="text-xs text-gray-400 w-8">DH</span>
                    </div>
                    {parseFloat(saleForm.p_cheque || 0) > 0 && (
                      <input type="text" value={saleForm.p_cheque_ref || ''} onChange={e => setSaleForm(f => ({...f, p_cheque_ref: e.target.value}))}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none ml-27" placeholder="N. cheque / banque" />
                    )}

                    {/* Carte */}
                    <div className="flex items-center gap-3">
                      <div className="w-24 text-sm font-semibold text-gray-700">Carte</div>
                      <input type="text" inputMode="decimal" value={saleForm.p_carte || ''} onFocus={e => e.target.select()}
                        onChange={e => setSaleForm(f => ({...f, p_carte: e.target.value}))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-300 focus:outline-none" placeholder="0.00" />
                      <span className="text-xs text-gray-400 w-8">DH</span>
                    </div>

                    {/* Virement */}
                    <div className="flex items-center gap-3">
                      <div className="w-24 text-sm font-semibold text-gray-700">Virement</div>
                      <input type="text" inputMode="decimal" value={saleForm.p_virement || ''} onFocus={e => e.target.select()}
                        onChange={e => setSaleForm(f => ({...f, p_virement: e.target.value}))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-300 focus:outline-none" placeholder="0.00" />
                      <span className="text-xs text-gray-400 w-8">DH</span>
                    </div>

                    {/* Achat Or */}
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-24 text-sm font-semibold text-amber-700">Achat Or</div>
                        <div className="flex-1 text-right text-sm font-bold text-amber-700">{orMontant > 0 ? `${fmt(orMontant)} DH` : ''}</div>
                        <span className="text-xs text-gray-400 w-8"></span>
                      </div>
                      <div className="flex items-center gap-2 ml-0">
                        <input type="text" inputMode="decimal" value={saleForm.p_or_poids || ''} onFocus={e => e.target.select()}
                          onChange={e => setSaleForm(f => ({...f, p_or_poids: e.target.value}))}
                          className="w-24 px-2 py-1.5 border border-amber-200 rounded-lg text-xs text-right bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-300" placeholder="Poids (g)" />
                        <span className="text-[10px] text-gray-400">g x</span>
                        <input type="text" inputMode="decimal" value={saleForm.p_or_prix_g || ''} onFocus={e => e.target.select()}
                          onChange={e => setSaleForm(f => ({...f, p_or_prix_g: e.target.value}))}
                          className="w-24 px-2 py-1.5 border border-amber-200 rounded-lg text-xs text-right bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-300" placeholder="Prix/g" />
                        <span className="text-[10px] text-gray-400">DH/g</span>
                      </div>
                    </div>

                    {/* Totaux */}
                    <div className="border-t-2 border-gray-300 pt-3 space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total encaisse</span>
                        <span className="font-bold text-green-700">{fmt(totalPaye)} DH</span>
                      </div>

                      {/* Trop percu → avoir client ou monnaie */}
                      {monnaie > 0 && selectedClient && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 space-y-1.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-700 font-medium">Trop percu</span>
                            <span className="font-bold text-blue-700">{fmt(monnaie)} DH</span>
                          </div>
                          <div className="flex gap-2">
                            <label className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border cursor-pointer text-xs font-semibold transition-all ${
                              saleForm.avoir_client ? 'bg-green-100 border-green-400 text-green-800' : 'bg-white border-gray-200 text-gray-600 hover:border-green-300'
                            }`}>
                              <input type="radio" name="trop_percu" checked={!!saleForm.avoir_client}
                                onChange={() => setSaleForm(f => ({...f, avoir_client: true}))}
                                className="sr-only" />
                              <span>Avoir client</span>
                            </label>
                            <label className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border cursor-pointer text-xs font-semibold transition-all ${
                              !saleForm.avoir_client ? 'bg-gray-100 border-gray-400 text-gray-800' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}>
                              <input type="radio" name="trop_percu" checked={!saleForm.avoir_client}
                                onChange={() => setSaleForm(f => ({...f, avoir_client: false}))}
                                className="sr-only" />
                              <span>Rendre monnaie</span>
                            </label>
                          </div>
                        </div>
                      )}
                      {monnaie > 0 && !selectedClient && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Monnaie a rendre</span>
                          <span className="font-bold text-blue-600">{fmt(monnaie)} DH</span>
                        </div>
                      )}

                      {/* Pas assez → credit client */}
                      {reste > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Restant du</span>
                            <span className="font-bold text-red-600">{fmt(reste)} DH</span>
                          </div>
                          {selectedClient && (
                            <label className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 cursor-pointer">
                              <input type="checkbox" checked={!!saleForm.mettre_en_compte}
                                onChange={e => setSaleForm(f => ({...f, mettre_en_compte: e.target.checked}))}
                                className="rounded border-gray-300 text-purple-600" />
                              <div className="flex-1">
                                <span className="text-sm font-semibold text-purple-700">Credit accorde</span>
                                <span className="text-sm font-bold text-purple-900 ml-2">{fmt(reste)} DH</span>
                              </div>
                            </label>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Cross-boutique warning + option transfert */}
            {!isCommande && !isLocal && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                <div>
                  <div className="text-sm font-semibold text-amber-800 mb-1">⚠️ Pièce dans une autre boutique</div>
                  <div className="text-xs text-amber-700">
                    Cette pièce est à <strong>{piece.boutique_nom}</strong>.
                    Vous pouvez vendre directement ou demander un transfert d'abord.
                  </div>
                </div>
                {boutiqueId && (
                  <button
                    onClick={() => demanderTransfert(piece, piece.modele || {})}
                    disabled={transfertEnCours}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Truck className="h-4 w-4" />
                    {transfertEnCours ? 'Demande en cours...' : 'Demander le transfert vers ma boutique'}
                  </button>
                )}
              </div>
            )}

            {/* Observation */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Observation (optionnel)</div>
              <textarea
                rows={2}
                value={saleForm.obs}
                onChange={e => setSaleForm(f => ({ ...f, obs: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:border-blue-400"
                placeholder="Notes sur la vente..."
              />
            </div>

            {/* Actions */}
            {saleStep === 1 ? (
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { setSalePiece(null); setIsCommande(false); setSelectedClient(null); }}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => setSaleStep(2)}
                  disabled={!saleForm.prix || parseFloat(saleForm.prix) <= 0}
                  className={`flex-1 py-3 text-white rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors ${
                    isCommande ? 'bg-violet-600 hover:bg-violet-700' : 'bg-gray-800 hover:bg-gray-900'
                  }`}
                >
                  Récapitulatif →
                </button>
              </div>
            ) : (
              <div className="pt-1 space-y-3">
                {/* Recapitulatif avant confirmation */}
                <div className={`rounded-2xl p-4 border ${overPvp ? 'bg-red-50 border-red-200' : isCommande ? 'bg-violet-50 border-violet-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Recapitulatif de la vente</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bijou</span>
                      <span className="font-semibold text-gray-800 font-mono">{isCommande ? piece.reference_article : piece.id_produit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Client</span>
                      <span className="font-semibold text-gray-800">{saleForm.client_nom || <span className="italic text-gray-400">Anonyme</span>}</span>
                    </div>
                    {saleForm.client_tel && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Telephone</span>
                        <span className="font-semibold text-gray-800">{saleForm.client_tel}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Paiement</span>
                      <span className="font-semibold text-gray-800 capitalize">
                        {paiements.length > 1 ? `${paiements.length} moyens` : paiements.length === 1 ? paiements[0].mode : saleForm.paiement}
                      </span>
                    </div>
                    {paiements.length > 0 && (
                      <div className="space-y-0.5 text-xs text-gray-500 pl-2">
                        {paiements.map((p, i) => (
                          <div key={i} className="flex justify-between">
                            <span className="capitalize">{p.mode}{p.reference ? ` (${p.reference})` : ''}</span>
                            <span>{fmt(parseFloat(p.montant || 0))} DH</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t pt-2 space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Prix bijou</span>
                        <span className="font-semibold text-gray-800">{fmt(prixV)} DH</span>
                      </div>
                      {(remiseM > 0 || remiseP > 0) && (
                        <div className="flex justify-between text-orange-600">
                          <span>Remise {remiseP > 0 ? `(${parseFloat(remiseP).toFixed(1)}%)` : ''}</span>
                          <span className="font-semibold">-{fmt(prixV - prixApresRemise)} DH</span>
                        </div>
                      )}
                    </div>
                    {/* Reglement recap */}
                    {(() => {
                      const esp = parseFloat(saleForm.p_especes || 0);
                      const chq = parseFloat(saleForm.p_cheque || 0);
                      const crt = parseFloat(saleForm.p_carte || 0);
                      const vir = parseFloat(saleForm.p_virement || 0);
                      const orP = parseFloat(saleForm.p_or_poids || 0) * parseFloat(saleForm.p_or_prix_g || 0);
                      const cpt = parseFloat(saleForm.p_compte || 0);
                      const tot = esp + chq + crt + vir + orP + cpt;
                      const rst = Math.max(0, sousTotal - tot);
                      return (
                        <div className="border-t pt-2 space-y-1">
                          {esp > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Especes</span><span>{fmt(esp)} DH</span></div>}
                          {chq > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Cheque {saleForm.p_cheque_ref ? `(${saleForm.p_cheque_ref})` : ''}</span><span>{fmt(chq)} DH</span></div>}
                          {crt > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Carte</span><span>{fmt(crt)} DH</span></div>}
                          {vir > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Virement</span><span>{fmt(vir)} DH</span></div>}
                          {orP > 0 && <div className="flex justify-between text-sm text-amber-700"><span>Or ({parseFloat(saleForm.p_or_poids||0).toFixed(3)}g x {fmt(parseFloat(saleForm.p_or_prix_g||0))}/g)</span><span>{fmt(orP)} DH</span></div>}
                          {cpt > 0 && <div className="flex justify-between text-sm text-purple-700"><span>Compte client</span><span>{fmt(cpt)} DH</span></div>}
                          <div className="border-t pt-1 flex justify-between items-center">
                            <span className="font-bold text-gray-700">Total encaisse</span>
                            <span className="text-lg font-bold text-green-700">{fmt(tot)} DH</span>
                          </div>
                          {rst > 0 && saleForm.mettre_en_compte && (
                            <div className="flex justify-between text-sm bg-red-50 -mx-4 px-4 py-1.5 rounded">
                              <span className="font-bold text-red-700">Credit accorde</span>
                              <span className="font-bold text-red-700">{fmt(rst)} DH</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {overPvp && (
                  <p className="text-xs text-red-500 font-semibold text-center">Prix superieur au PVP conseille ({fmt(pvpRef)} DH)</p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => setSaleStep(1)}
                    className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    ← Modifier
                  </button>
                  <button
                    onClick={confirmSale}
                    disabled={selling}
                    className={`flex-1 py-3 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${
                      overPvp ? 'bg-red-600 hover:bg-red-700' :
                      isCommande ? 'bg-violet-600 hover:bg-violet-700' :
                      'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {selling
                      ? <><RefreshCw className="h-4 w-4 animate-spin" /> En cours…</>
                      : isCommande
                        ? <><ShoppingBag className="h-4 w-4" /> Confirmer la commande</>
                        : <><Check className="h-4 w-4" /> ✓ Valider la vente</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 px-4 sm:px-6 py-3">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Row 1 on mobile: title + boutique + refresh */}
          <div className="flex items-center gap-3">
            {/* Title */}
            <div className="flex items-center gap-2 mr-1 shrink-0">
              <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-sm">
                <ShoppingBag className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-bold text-gray-800 text-sm leading-tight">Vente Bijoux</div>
                <div className="text-xs text-gray-400">Point de vente</div>
              </div>
            </div>

            {/* Boutique selector */}
            <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl bg-white hover:border-blue-300 transition-colors flex-1 sm:flex-none sm:min-w-[180px]">
              <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
              <select
                value={boutiqueId || ''}
                onChange={e => setBoutiqueId(e.target.value ? parseInt(e.target.value) : null)}
                className="flex-1 outline-none text-sm bg-transparent cursor-pointer"
              >
                <option value="">Toutes les boutiques</option>
                {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
              </select>
            </div>

            {/* Refresh — visible inline on mobile */}
            <button
              onClick={loadModeles}
              className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors shrink-0"
              title="Actualiser"
            >
              <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Search — full width on mobile, grows on desktop */}
          <div className="flex-1 sm:min-w-[220px] sm:max-w-lg">
            <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-xl bg-white focus-within:border-blue-400 transition-colors">
              <Search className="h-4 w-4 text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Référence, nom ou N° unique (ex: 1.0063)..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-1 outline-none text-sm bg-transparent"
              />
              {search && (
                <button onClick={() => setSearch('')} className="hover:text-gray-600">
                  <X className="h-4 w-4 text-gray-300" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ──────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-gray-800 via-gray-800 to-gray-900 text-white px-4 sm:px-6 py-3">
        <div className="max-w-screen-2xl mx-auto flex items-center gap-4 sm:gap-6 flex-wrap text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-gray-400">Stock ici:</span>
            <span className="font-bold text-green-400 text-base">{stats.ici}</span>
            <span className="text-gray-500 text-xs">pièces</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-gray-400">Réseau:</span>
            <span className="font-bold text-amber-400 text-base">{stats.reseau}</span>
            <span className="text-gray-500 text-xs">pièces</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="text-gray-400">Catalogue:</span>
            <span className="font-bold text-blue-400 text-base">{stats.total}</span>
            <span className="text-gray-500 text-xs">modèles</span>
          </div>
          {boutique && (
            <div className="ml-auto flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
              <Store className="h-3.5 w-3.5 text-amber-300" />
              <span className="text-amber-200 font-semibold text-xs">{boutique.nom}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── CONTENU PRINCIPAL ───────────────────────────────────────────────── */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-5">

        {/* ══════════════════════ PHASE CLIENT ══════════════════════ */}
        {ventePhase === 'client' && !salePiece && (
          <div className="max-w-xl mx-auto py-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <User className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Qui est le client ?</h2>
              <p className="text-gray-500 mt-1">Cherchez un client existant ou créez-en un nouveau</p>
            </div>

            {/* Recherche client */}
            <div className="relative mb-4">
              <div className="flex items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus-within:border-blue-400 transition-colors">
                <Search className="h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom, prénom ou téléphone du client..."
                  value={saleForm.client_nom}
                  onChange={e => { setSaleForm(f => ({...f, client_nom: e.target.value})); searchClients(e.target.value); }}
                  onFocus={() => saleForm.client_nom && searchClients(saleForm.client_nom)}
                  className="flex-1 outline-none text-base bg-transparent"
                  autoFocus
                />
              </div>
              {showClientSugg && clientSuggestions.length > 0 && (
                <div className="absolute z-50 w-full bg-white border-2 border-blue-200 rounded-xl mt-1 shadow-xl max-h-72 overflow-y-auto">
                  {clientSuggestions.map(c => (
                    <button
                      key={c.id}
                      onClick={() => selectClient(c)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                    >
                      <div className="font-semibold text-gray-800">{c.nom}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-3">
                        {c.telephone && <span><Phone className="inline h-3 w-3 mr-1" />{c.telephone}</span>}
                        {c.email && <span>{c.email}</span>}
                        {parseFloat(c.solde_or_grammes) > 0 && <span className="text-amber-600 font-medium">Or: {parseFloat(c.solde_or_grammes).toFixed(1)}g</span>}
                        {parseFloat(c.solde_compte) !== 0 && <span className={parseFloat(c.solde_compte) > 0 ? 'text-green-600' : 'text-red-600'}>Compte: {fmt(c.solde_compte)} DH</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Bouton créer nouveau client */}
            {!showNewClientForm ? (
              <button
                onClick={() => setShowNewClientForm(true)}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors font-medium"
              >
                + Nouveau client
              </button>
            ) : (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-3">
                <div className="font-semibold text-blue-800 text-sm">Créer un client</div>
                <input
                  type="text" placeholder="Nom *"
                  value={newClientInSale.nom}
                  onChange={e => setNewClientInSale(f => ({...f, nom: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  autoFocus
                />
                <input
                  type="text" placeholder="Téléphone"
                  value={newClientInSale.telephone}
                  onChange={e => setNewClientInSale(f => ({...f, telephone: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <div className="flex gap-2">
                  <button onClick={() => setShowNewClientForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
                  <button onClick={createClientInSale} disabled={savingNewClient || !newClientInSale.nom.trim()} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                    {savingNewClient ? 'Création...' : 'Créer et continuer'}
                  </button>
                </div>
              </div>
            )}

            {/* Accès direct au catalogue sans client */}
            <button
              onClick={() => setVentePhase('catalogue')}
              className="w-full mt-6 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Passer sans client (consultation catalogue)
            </button>
          </div>
        )}

        {/* ══════════════════════ PHASE CATALOGUE (bijoux en mode liste) ══════════════════════ */}
        {ventePhase === 'catalogue' && !salePiece && (
          <>
            {/* Bandeau client — discret */}
            <div className="flex items-center gap-2 mb-3 text-sm">
              {selectedClient ? (
                <>
                  <User className="h-4 w-4 text-blue-500" />
                  <span className="font-semibold text-gray-700">{selectedClient.nom}</span>
                  <span className="text-gray-300">|</span>
                  <button onClick={resetVente} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Changer</button>
                </>
              ) : (
                <button onClick={() => setVentePhase('client')} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Choisir un client</button>
              )}
            </div>

            {/* Recherche */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Chercher par réf, nom, n° unique (1.XXXX)..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  autoFocus
                />
              </div>
              <select value={fType} onChange={e => setFType(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Tous types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Liste bijoux — par défaut ceux en boutique, recherche ouvre tout */}
            {(() => {
              // De base : seulement les bijoux EN boutique. Si recherche, tout le catalogue.
              const listeAffichee = (search ? filtered : filtered.filter(m => piecesOf(m).ici.length > 0)).slice(0, 100);
              const imgBase = API_URL.replace('/api', '');

              return (
                <div className="space-y-2">
                  <div className="text-xs text-gray-400 mb-2">
                    {search
                      ? `${listeAffichee.length} résultat${listeAffichee.length > 1 ? 's' : ''} dans tout le catalogue`
                      : `${listeAffichee.length} bijou${listeAffichee.length > 1 ? 'x' : ''} en boutique`
                    }
                  </div>

                  {listeAffichee.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <Gem className="h-10 w-10 mx-auto mb-3 opacity-30" />
                      <p>{search ? 'Aucun bijou trouvé' : 'Aucun bijou en boutique'}</p>
                      {!search && <p className="text-xs mt-1">Utilisez la recherche pour trouver dans tout le catalogue</p>}
                    </div>
                  ) : listeAffichee.map(m => {
                    const p = piecesOf(m);
                    const stockIci = p.ici.length;
                    const stockReseau = p.ailleurs.length;
                    const pvp = m.pvp_magasin || m.prix_vente || m.prix_base || 0;
                    const pieceIci = p.ici[0];
                    const imgUrl = m.image_url ? (m.image_url.startsWith('/') ? `${imgBase}${m.image_url}` : m.image_url) : null;
                    const typeIcon = TYPE_ICON[m.type_bijou?.toUpperCase()] || '💍';

                    return (
                      <div
                        key={m.reference_article}
                        className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                        onClick={() => { setSelectedModele(m); setPanelPhase(1); }}
                      >
                        {/* Photo miniature */}
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                          {imgUrl ? (
                            <img src={imgUrl} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                          ) : null}
                          <span className={`text-xl ${imgUrl ? 'hidden' : ''}`}>{typeIcon}</span>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 truncate">{m.nom_modele || m.reference_article}</span>
                            <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{m.reference_article}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                            {m.collection_nom && <span>{m.collection_nom}</span>}
                            <span>{m.type_bijou || '—'}</span>
                            {m.poids_metal > 0 && <span>{parseFloat(m.poids_metal).toFixed(1)}g</span>}
                            {m.pieces?.[0]?.id_produit && <span className="font-mono text-gray-400">{m.pieces[0].id_produit}</span>}
                          </div>
                        </div>

                        {/* Prix */}
                        <div className="text-right shrink-0 mr-3">
                          <div className="font-bold text-gray-800">{pvp > 0 ? `${fmt(pvp)} DH` : '—'}</div>
                        </div>

                        {/* Stock badges */}
                        <div className="flex flex-col items-end gap-1 shrink-0 mr-3 min-w-[60px]">
                          {stockIci > 0 && <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">{stockIci} ici</span>}
                          {stockReseau > 0 && <span className="text-xs bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">{stockReseau} autre</span>}
                          {stockIci === 0 && stockReseau === 0 && <span className="text-xs text-gray-300">—</span>}
                        </div>

                        {/* Bouton vendre */}
                        {pieceIci && selectedClient ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); openSale(pieceIci, m); }}
                            className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-xl hover:bg-green-700 transition-colors shrink-0 opacity-80 group-hover:opacity-100"
                          >
                            Vendre
                          </button>
                        ) : (
                          <div className="w-20 shrink-0" />
                        )}
                      </div>
                    );
                  })}

                  {(search ? filtered : filtered.filter(m => piecesOf(m).ici.length > 0)).length > 100 && (
                    <div className="text-center py-2 text-xs text-gray-400">Affichage limité à 100 — affinez la recherche</div>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* ══════════════════════ ANCIEN CONTENU (collections/cards) — masqué en mode vente ══════════════════════ */}

        {/* ── VUE COLLECTIONS (quand pas de recherche active et pas de collection sélectionnée) ── */}
        {ventePhase !== 'client' && ventePhase !== 'catalogue' && !search && !fType && fDispo === 'tous' && !selectedCollection && !fCol && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Collections</h2>
                <p className="text-sm text-gray-400 mt-0.5">{collectionGroups.length} collection{collectionGroups.length > 1 ? 's' : ''} · {modeles.length} modèles</p>
              </div>
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <RefreshCw className="h-10 w-10 animate-spin text-gray-300 mb-4" />
                <p className="text-gray-500">Chargement…</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {collectionGroups.map(col => (
                  <div
                    key={col.key}
                    onClick={() => setSelectedCollection(col.key)}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-200 group"
                  >
                    {/* Image ou gradient */}
                    <div className={`h-44 bg-gradient-to-br ${col.grad} relative flex items-center justify-center overflow-hidden`}>
                      {col.image_url ? (
                        <img
                          src={col.image_url.startsWith('/') ? `${API_URL.replace("/api","")}${col.image_url}` : col.image_url}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          alt={col.nom}
                          onError={e => { e.target.style.display='none'; }}
                        />
                      ) : (
                        <span className="text-5xl drop-shadow-lg select-none opacity-80">💎</span>
                      )}
                      {/* Badges stock */}
                      <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                        {col.stock_ici > 0 && (
                          <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow">✓ {col.stock_ici} ici</span>
                        )}
                        {col.stock_reseau > 0 && (
                          <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow">◎ {col.stock_reseau} réseau</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="font-bold text-gray-800 text-base truncate">{col.nom}</div>
                      <div className="text-sm text-gray-400 mt-0.5">{col.nb_modeles} modèle{col.nb_modeles > 1 ? 's' : ''}</div>
                      <div className="mt-2 flex items-center gap-1 text-xs">
                        {col.stock_ici + col.stock_reseau > 0
                          ? <span className="text-green-600 font-semibold">{col.stock_ici + col.stock_reseau} pièce{col.stock_ici+col.stock_reseau>1?'s':''} dispo</span>
                          : <span className="text-gray-400">Sur catalogue</span>
                        }
                        <span className="ml-auto text-gray-300 group-hover:text-gray-500 transition-colors">→</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── VUE MODÈLES (collection sélectionnée ou filtre actif) ── */}
        {(search || fType || fDispo !== 'tous' || selectedCollection || fCol) && (
          <>
            {/* Header avec breadcrumb + filtres */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              {selectedCollection && !search && (
                <button
                  onClick={() => { setSelectedCollection(null); setFCol(''); }}
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-xl transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Collections
                </button>
              )}
              {selectedCollection && !search && (
                <span className="font-bold text-gray-800 text-lg">
                  {collectionGroups.find(c => c.key === selectedCollection)?.nom || selectedCollection}
                </span>
              )}

              <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
                <select
                  value={fType}
                  onChange={e => setFType(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none hover:border-gray-300 cursor-pointer"
                >
                  <option value="">Tous types</option>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>

                <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden text-sm">
                  {[{ v:'tous', label:'Tous' }, { v:'ici', label:'📍 Ici' }, { v:'reseau', label:'🌍 Réseau' }].map(opt => (
                    <button key={opt.v} onClick={() => setFDispo(opt.v)}
                      className={`px-3 py-2 transition-all ${fDispo===opt.v ? 'bg-gray-800 text-white font-semibold' : 'text-gray-500 hover:bg-gray-50'}`}
                    >{opt.label}</button>
                  ))}
                </div>

                {(search || fCol || fType || fDispo !== 'tous') && (
                  <button onClick={resetFilters} className="flex items-center gap-1 text-sm text-red-500 border border-red-200 rounded-xl px-3 py-2 hover:bg-red-50 transition-colors">
                    <X className="h-3.5 w-3.5" /> Reset
                  </button>
                )}
                <span className="text-sm text-gray-400">{filtered.length} modèle{filtered.length !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Grille ou tableau modeles */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <RefreshCw className="h-10 w-10 animate-spin text-gray-300 mb-4" />
                <p className="text-gray-500">Chargement...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <Gem className="h-16 w-16 text-gray-200 mb-4" />
                <p className="text-gray-500 font-medium text-lg">Aucun modele trouve</p>
                <button onClick={() => { resetFilters(); setSelectedCollection(null); }}
                  className="mt-4 px-5 py-2 bg-gray-800 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors">
                  Voir tout le catalogue
                </button>
              </div>
            ) : search ? (
              /* Compact table view for search results */
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Ref</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Poids</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">PVP</th>
                      <th className="text-center px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Stock</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(m => {
                      const { ici, ailleurs } = piecesOf(m);
                      const pvp = m.pvp_magasin || m.prix_vente || m.prix_base;
                      const firstP = [...ici, ...ailleurs][0] || (m.pieces || [])[0];
                      const poids = firstP?.poids_metal;
                      const idProduit = (m.pieces || []).map(p => p.id_produit).filter(Boolean).join(', ');
                      return (
                        <tr
                          key={m.id || m.reference_article}
                          onClick={() => setSelectedModele(m)}
                          className="hover:bg-blue-50 cursor-pointer transition-colors group"
                        >
                          <td className="px-3 py-2.5">
                            <div className="font-mono text-xs font-bold text-purple-700">{m.reference_article}</div>
                            {idProduit && <div className="font-mono text-[11px] text-gray-400 truncate max-w-[120px]">{idProduit}</div>}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-semibold text-gray-800 text-sm truncate max-w-[200px]">{m.nom_modele || m.type_bijou || '---'}</div>
                            {m.collection_nom && <div className="text-xs text-gray-400 truncate">{m.collection_nom}</div>}
                          </td>
                          <td className="px-3 py-2.5 hidden sm:table-cell">
                            <span className="text-xs text-gray-600">{m.type_bijou || '---'}</span>
                          </td>
                          <td className="px-3 py-2.5 text-right hidden md:table-cell">
                            {poids ? <span className="font-mono text-xs text-gray-600">{parseFloat(poids).toFixed(2)}g</span> : <span className="text-gray-300">---</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {pvp ? <span className="font-bold text-gray-900">{fmt(pvp)}</span> : <span className="text-gray-300">---</span>}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {ici.length > 0 && (
                                <span className="bg-green-100 text-green-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{ici.length} ici</span>
                              )}
                              {ailleurs.length > 0 && (
                                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{ailleurs.length} res.</span>
                              )}
                              {ici.length === 0 && ailleurs.length === 0 && (
                                <span className="text-xs text-gray-400">Cat.</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
                {filtered.map(m => <ModelCard key={m.id || m.reference_article} m={m} />)}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODEL DETAIL PANEL ──────────────────────────────────────────────── */}
      {selectedModele && ModelPanel({ m: selectedModele })}

      {/* ── SALE MODAL ─────────────────────────────────────────────────────── */}
      {salePiece && SaleModal({ piece: salePiece })}

      {/* ── MODAL TRANSFERT CLIENT ─────────────────────────────────────────── */}
      {showTransfertModal && pendingTransfert && (
        <TransfertClientModal
          piece={pendingTransfert.piece}
          modele={pendingTransfert.modele}
          type={pendingTransfert.type}
          onClose={() => { setShowTransfertModal(false); setPendingTransfert(null); }}
          onConfirm={confirmerTransfert}
        />
      )}
    </div>
  );
}

// ─── Modal intermédiaire : "Pour quel client ?" ────────────────────────────────

function TransfertClientModal({ piece, modele, type, onClose, onConfirm }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [client, setClient]       = useState(null);
  const [venduAvance, setVenduAvance] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(null); // { id } après succès
  const searchRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clients`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const all = await res.json();
        const q = query.toLowerCase();
        setResults((Array.isArray(all) ? all : []).filter(c =>
          (c.nom && c.nom.toLowerCase().includes(q)) ||
          (c.telephone && c.telephone.includes(q))
        ).slice(0, 6));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const handleConfirm = async () => {
    setSaving(true);
    const result = await onConfirm({ client, venduAvance });
    setSaving(false);
    if (result) setDone(result);
  };

  const refLabel = piece?.id_produit || modele?.reference_article || '—';
  const source   = piece?.boutique_nom || (type === 'commande' ? 'Central / Atelier' : 'Réseau');

  // État succès
  if (done) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <Truck className="h-10 w-10 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Transfert demandé !</h3>
          <p className="text-sm text-gray-500 mb-2">
            Demande n°<strong>{done.id}</strong>
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-left space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Pièce</span>
              <span className="font-mono font-semibold text-gray-800">{refLabel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Depuis</span>
              <span className="font-semibold text-gray-700">{source}</span>
            </div>
            {client && (
              <div className="flex justify-between">
                <span className="text-gray-500">Client</span>
                <span className={`font-semibold ${venduAvance ? 'text-green-700' : 'text-purple-700'}`}>
                  {client.nom} {venduAvance ? '· Payé ✓' : '· À récupérer'}
                </span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-5">
            {client && venduAvance
              ? `La pièce sera réservée pour ${client.nom} dès réception.`
              : client
                ? `${client.nom} sera contacté(e) à l'arrivée de la pièce.`
                : 'La demande a été transmise au central.'}
          </p>
          <button onClick={onClose}
            className="w-full py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-700 transition-colors">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-start justify-between">
          <div>
            <div className="font-bold text-lg flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {type === 'commande' ? 'Demande au central' : 'Transfert réseau'}
            </div>
            <div className="text-blue-200 text-xs mt-0.5 font-mono">{refLabel}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Info pièce */}
          <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Gem className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-800 text-sm">{modele?.nom_modele || modele?.reference_article}</div>
              <div className="text-xs text-gray-400">
                {piece ? `Depuis : ${source}` : `Depuis : ${source}`}
                {modele?.pvp_magasin ? ` · ${parseFloat(modele.pvp_magasin).toLocaleString('fr-FR')} DH` : ''}
              </div>
            </div>
          </div>

          {/* Recherche client */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pour quel client ?
              <span className="font-normal text-gray-400 ml-1">(optionnel)</span>
            </label>

            {client ? (
              <div className="flex items-center gap-3 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-green-700">{(client.nom || '?')[0].toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 text-sm">{client.nom}</div>
                  {client.telephone && <div className="text-xs text-gray-500">{client.telephone}</div>}
                </div>
                <button onClick={() => setClient(null)} className="text-gray-400 hover:text-red-500">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Nom ou téléphone du client..."
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  autoFocus
                />
                {results.length > 0 && (
                  <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                    {results.map(c => (
                      <button key={c.id} onClick={() => { setClient(c); setQuery(''); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 text-left border-b border-gray-50 last:border-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-blue-600">{(c.nom || '?')[0].toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-800 text-sm">{c.nom}</div>
                          {c.telephone && <div className="text-xs text-gray-400">{c.telephone}</div>}
                        </div>
                        <Check className="h-4 w-4 text-blue-400 ml-auto opacity-0 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Option : déjà payé */}
          {client && (
            <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
              <div className="relative mt-0.5 flex-shrink-0">
                <input type="checkbox" checked={venduAvance} onChange={e => setVenduAvance(e.target.checked)} className="sr-only" />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${venduAvance ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'}`}>
                  {venduAvance && <Check className="h-3 w-3 text-white" />}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  Bijou déjà payé / vendu à l'avance
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Cochez si {client.nom} a déjà réglé. La pièce lui sera réservée à réception.
                </div>
              </div>
            </label>
          )}

          {!client && (
            <p className="text-xs text-gray-400 text-center">
              Sans client sélectionné, la demande sera transmise sans réservation
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 transition-colors text-sm">
            Annuler
          </button>
          <button onClick={handleConfirm} disabled={saving}
            className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
            {client ? `Réserver pour ${client.nom.split(' ')[0]}` : 'Envoyer la demande'}
          </button>
        </div>
      </div>
    </div>
  );
}
